import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowRight, 
  Bot, 
  GitBranch, 
  ShieldCheck, 
  Network,
  Play,
  Check,
  Clock,
  Zap,
  Building2,
  Code2,
  Users,
  TrendingDown,
  BarChart3,
  FileCheck,
  MessageSquare,
  HandshakeIcon,
  ChevronRight,
  Cpu,
  Workflow,
  FileText,
  Shield,
} from 'lucide-react';

const HomeNew = () => {
  const navigate = useNavigate();
  
  return (
    <div className="bg-[#F6F7FB] min-h-screen">
      <Helmet>
        <title>Liquicent - AI Agents for Enterprise Workflows</title>
        <meta name="description" content="Liquicent deploys AI agents that automate complex workflows and reduce coordination costs across organizations. The registry for AI agents that run enterprise workflows." />
      </Helmet>

      {/* Hero Section - Updated per spec */}
      <HeroSection navigate={navigate} />
      
      {/* Platform Architecture - UTG Visualization */}
      <PlatformArchitecture />
      
      {/* Workflow Automation Section */}
      <WorkflowAutomationSection />
      
      {/* Workflow Simulator */}
      <WorkflowSimulator />
      
      {/* Agent Registry Section */}
      <AgentRegistrySection />
      
      {/* Platform Economics Section */}
      <PlatformEconomicsSection />
      
      {/* Developer Platform Section */}
      <DeveloperPlatformSection navigate={navigate} />
      
      {/* Research Section */}
      <ResearchSection />
      
      {/* Final CTA */}
      <FinalCTA navigate={navigate} />
    </div>
  );
};

// Hero Section - Updated with spec requirements
const HeroSection = ({ navigate }) => (
  <section className="relative pt-32 pb-24 lg:pt-40 lg:pb-32 overflow-hidden">
    {/* Animated Graph Background */}
    <div className="absolute inset-0 bg-gradient-to-b from-white to-[#F6F7FB]" />
    <AnimatedGraphBackground />
    
    <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
      <div className="max-w-4xl mx-auto text-center">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#1A3A8F]/5 rounded-full mb-8">
          <span className="w-2 h-2 bg-[#2CB1A1] rounded-full animate-pulse" />
          <span className="text-sm font-medium text-[#1A3A8F]">
            The Registry for AI Agents That Run Enterprise Workflows
          </span>
        </div>
        
        {/* Headline - Per Spec */}
        <h1 className="text-[48px] lg:text-[56px] font-bold text-[#0F172A] leading-[1.1] mb-6 tracking-tight">
          AI Agents for{' '}
          <span className="bg-gradient-to-r from-[#1A3A8F] to-[#2CB1A1] bg-clip-text text-transparent">
            Enterprise Workflows
          </span>
        </h1>
        
        {/* Subheadline - Per Spec */}
        <p className="text-xl text-[#475569] max-w-3xl mx-auto mb-4 leading-relaxed">
          Liquicent deploys AI agents that automate complex workflows and reduce coordination costs across organizations.
        </p>
        
        {/* Supporting Line */}
        <p className="text-base text-[#64748B] max-w-2xl mx-auto mb-10">
          From engineering approvals to customer support and procurement, Liquicent helps teams execute decisions faster while maintaining governance and traceability.
        </p>
        
        {/* 3 Primary CTAs - Per Spec */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button 
            onClick={() => navigate('/workflows/pr-governance')}
            className="bg-[#1A3A8F] hover:bg-[#142C6D] text-white px-6 py-6 text-base font-medium shadow-lg shadow-[#1A3A8F]/20 hover:shadow-xl transition-all rounded-lg"
          >
            Explore Workflows
            <ArrowRight className="ml-2 w-5 h-5" />
          </Button>
          <Button 
            onClick={() => navigate('/agents')}
            variant="outline"
            className="border-[#1A3A8F] text-[#1A3A8F] hover:bg-[#1A3A8F] hover:text-white px-6 py-6 text-base font-medium transition-all rounded-lg"
          >
            <Bot className="mr-2 w-5 h-5" />
            Browse Agents
          </Button>
          <Button 
            onClick={() => navigate('/developers')}
            variant="outline"
            className="border-[#E5E7EB] text-[#475569] hover:bg-[#F6F7FB] hover:text-[#1A3A8F] px-6 py-6 text-base font-medium transition-all rounded-lg"
          >
            <Code2 className="mr-2 w-5 h-5" />
            For Developers
          </Button>
        </div>
        
        {/* Trust Indicators */}
        <div className="mt-16 flex flex-wrap items-center justify-center gap-8 text-[#64748B]">
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-[#2CB1A1]" />
            <span className="text-sm">SOC 2 Compliant</span>
          </div>
          <div className="flex items-center gap-2">
            <Building2 className="w-5 h-5 text-[#2CB1A1]" />
            <span className="text-sm">Enterprise Ready</span>
          </div>
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#2CB1A1]" />
            <span className="text-sm">100+ Companies</span>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Animated Graph Background Component
const AnimatedGraphBackground = () => {
  const canvasRef = useRef(null);
  
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const nodes = [];
    const edges = [];
    
    // Set canvas size
    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = 600;
    };
    resize();
    window.addEventListener('resize', resize);
    
    // Create nodes
    for (let i = 0; i < 20; i++) {
      nodes.push({
        x: Math.random() * canvas.width,
        y: Math.random() * canvas.height,
        radius: 4 + Math.random() * 4,
        opacity: 0,
        targetOpacity: 0.3 + Math.random() * 0.4,
        vx: (Math.random() - 0.5) * 0.3,
        vy: (Math.random() - 0.5) * 0.3,
      });
    }
    
    // Create edges between nearby nodes
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const dx = nodes[i].x - nodes[j].x;
        const dy = nodes[i].y - nodes[j].y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200) {
          edges.push({ from: i, to: j, opacity: 0 });
        }
      }
    }
    
    let frame = 0;
    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      frame++;
      
      // Update nodes
      nodes.forEach((node, i) => {
        // Fade in gradually
        if (frame > i * 10 && node.opacity < node.targetOpacity) {
          node.opacity += 0.01;
        }
        
        // Move slowly
        node.x += node.vx;
        node.y += node.vy;
        
        // Bounce off edges
        if (node.x < 0 || node.x > canvas.width) node.vx *= -1;
        if (node.y < 0 || node.y > canvas.height) node.vy *= -1;
        
        // Draw node
        ctx.beginPath();
        ctx.arc(node.x, node.y, node.radius, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(26, 58, 143, ${node.opacity})`;
        ctx.fill();
      });
      
      // Draw edges
      edges.forEach((edge, i) => {
        if (frame > i * 5 && edge.opacity < 0.15) {
          edge.opacity += 0.005;
        }
        
        const from = nodes[edge.from];
        const to = nodes[edge.to];
        
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x, to.y);
        ctx.strokeStyle = `rgba(44, 177, 161, ${edge.opacity})`;
        ctx.lineWidth = 1;
        ctx.stroke();
      });
      
      requestAnimationFrame(animate);
    };
    
    animate();
    
    return () => window.removeEventListener('resize', resize);
  }, []);
  
  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 pointer-events-none"
      style={{ opacity: 0.6 }}
    />
  );
};

// Platform Architecture - UTG Visualization
const PlatformArchitecture = () => {
  const [activeStep, setActiveStep] = useState(0);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveStep((prev) => (prev + 1) % 4);
    }, 2000);
    return () => clearInterval(interval);
  }, []);
  
  const steps = [
    { 
      id: 'agents',
      icon: Bot, 
      title: 'Agents', 
      description: 'AI agents that execute tasks (PR verification, ticket routing, deal validation, vendor risk checks)',
      color: '#1A3A8F'
    },
    { 
      id: 'workflows',
      icon: Workflow, 
      title: 'Workflows', 
      description: 'Enterprise processes automated by agents (engineering governance, customer support, sales approvals)',
      color: '#2CB1A1'
    },
    { 
      id: 'decisions',
      icon: FileCheck, 
      title: 'DecisionTrace', 
      description: 'Records of decisions and approvals with complete audit trails',
      color: '#1A3A8F'
    },
    { 
      id: 'utg',
      icon: Network, 
      title: 'Unified Transaction Graph', 
      description: 'Coordination graph created from workflows - a single source of truth',
      color: '#2CB1A1'
    },
  ];

  return (
    <section className="py-24 lg:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-[32px] font-bold text-[#0F172A] mb-4">
            The Liquicent Coordination Platform
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">
            A unified architecture that transforms how enterprises coordinate
          </p>
        </div>

        {/* Animated Flow Visualization */}
        <div className="relative max-w-5xl mx-auto">
          {/* Connection Lines */}
          <svg className="absolute top-1/2 left-0 right-0 -translate-y-1/2 h-2 w-full hidden lg:block" preserveAspectRatio="none">
            <defs>
              <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#1A3A8F" stopOpacity="0.3" />
                <stop offset="50%" stopColor="#2CB1A1" stopOpacity="0.5" />
                <stop offset="100%" stopColor="#1A3A8F" stopOpacity="0.3" />
              </linearGradient>
            </defs>
            <line x1="12%" y1="50%" x2="88%" y2="50%" stroke="url(#lineGradient)" strokeWidth="2" strokeDasharray="8 4" />
          </svg>
          
          {/* Steps */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
            {steps.map((step, index) => {
              const StepIcon = step.icon;
              const isActive = activeStep === index;
              
              return (
                <div
                  key={step.id}
                  className={`relative transition-all duration-500 ${isActive ? 'scale-105' : 'scale-100'}`}
                >
                  <Card 
                    className={`p-6 bg-white border-2 rounded-xl transition-all duration-500 ${
                      isActive 
                        ? 'border-[#2CB1A1] shadow-lg shadow-[#2CB1A1]/20' 
                        : 'border-[#E5E7EB] hover:border-[#1A3A8F]/30'
                    }`}
                    style={{ 
                      boxShadow: isActive ? '0px 6px 16px rgba(44, 177, 161, 0.15)' : '0px 6px 16px rgba(0,0,0,0.06)'
                    }}
                  >
                    {/* Step Number */}
                    <div className="absolute -top-3 -left-3 w-8 h-8 bg-white border-2 border-[#E5E7EB] rounded-full flex items-center justify-center shadow-sm">
                      <span className={`text-sm font-bold ${isActive ? 'text-[#2CB1A1]' : 'text-[#64748B]'}`}>
                        {index + 1}
                      </span>
                    </div>
                    
                    {/* Icon */}
                    <div 
                      className={`w-14 h-14 rounded-xl flex items-center justify-center mb-4 transition-all duration-500 ${
                        isActive ? 'scale-110' : ''
                      }`}
                      style={{ backgroundColor: `${step.color}15` }}
                    >
                      <StepIcon 
                        className={`w-7 h-7 transition-all duration-500 ${isActive ? 'animate-pulse' : ''}`} 
                        style={{ color: step.color }} 
                      />
                    </div>
                    
                    {/* Content */}
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{step.title}</h3>
                    <p className="text-sm text-[#64748B] leading-relaxed">{step.description}</p>
                  </Card>
                  
                  {/* Arrow connector for desktop */}
                  {index < steps.length - 1 && (
                    <div className="hidden lg:block absolute top-1/2 -right-3 transform -translate-y-1/2 z-10">
                      <ChevronRight className={`w-6 h-6 transition-colors duration-500 ${
                        activeStep > index ? 'text-[#2CB1A1]' : 'text-[#E5E7EB]'
                      }`} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Workflow Automation Section with Cards
const WorkflowAutomationSection = () => {
  const workflows = [
    { 
      id: 'pr-governance',
      icon: GitBranch, 
      title: 'PR Governance', 
      description: 'Automated code review and merge decision workflows',
      color: '#1A3A8F'
    },
    { 
      id: 'security-compliance',
      icon: Shield, 
      title: 'Security & Compliance', 
      description: 'Continuous security monitoring and compliance automation',
      color: '#2CB1A1'
    },
    { 
      id: 'customer-support',
      icon: MessageSquare, 
      title: 'Customer Support', 
      description: 'Intelligent ticket routing and resolution automation',
      color: '#1A3A8F'
    },
    { 
      id: 'sales-deal-desk',
      icon: HandshakeIcon, 
      title: 'Sales Deal Desk', 
      description: 'Streamlined deal approval and pricing governance',
      color: '#2CB1A1'
    },
    { 
      id: 'vendor-onboarding',
      icon: Building2, 
      title: 'Vendor Onboarding', 
      description: 'Automated vendor risk assessment and onboarding',
      color: '#1A3A8F'
    },
  ];

  return (
    <section className="py-24 bg-[#F6F7FB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-[32px] font-bold text-[#0F172A] mb-4">
            Automate Enterprise Workflows
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">
            Pre-built workflows that orchestrate AI agents across your organization
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {workflows.map((workflow) => {
            const WorkflowIcon = workflow.icon;
            return (
              <Link key={workflow.id} to={`/workflows/${workflow.id}`}>
                <Card 
                  className="p-6 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-lg hover:border-[#1A3A8F]/30 transition-all duration-300 h-full group"
                  style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
                >
                  <div 
                    className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${workflow.color}15` }}
                  >
                    <WorkflowIcon className="w-6 h-6" style={{ color: workflow.color }} />
                  </div>
                  <h3 className="text-lg font-semibold text-[#0F172A] mb-2 group-hover:text-[#1A3A8F] transition-colors">
                    {workflow.title}
                  </h3>
                  <p className="text-sm text-[#64748B] mb-4">{workflow.description}</p>
                  <span className="text-sm text-[#1A3A8F] font-medium inline-flex items-center">
                    View workflow
                    <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Card>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Workflow Simulator (kept from original but enhanced)
const WorkflowSimulator = () => {
  const [selectedWorkflow, setSelectedWorkflow] = useState('pr-governance');
  const [isRunning, setIsRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [completed, setCompleted] = useState(false);

  const workflows = {
    'pr-governance': {
      name: 'PR Governance',
      icon: GitBranch,
      steps: [
        { label: 'Developer submits PR', duration: 800 },
        { label: 'PR Verification Agent analyzes code', duration: 1500 },
        { label: 'Policy checks run', duration: 1200 },
        { label: 'Reviewer approval recorded', duration: 1000 },
        { label: 'Merge decision logged to UTG', duration: 800 },
      ],
    },
    'customer-support': {
      name: 'Customer Support',
      icon: MessageSquare,
      steps: [
        { label: 'Customer submits ticket', duration: 800 },
        { label: 'Ticket Classification Agent categorizes', duration: 1200 },
        { label: 'Knowledge Retrieval Agent finds solutions', duration: 1500 },
        { label: 'Response generated and reviewed', duration: 1200 },
        { label: 'Resolution recorded to UTG', duration: 800 },
      ],
    },
    'sales-deal-desk': {
      name: 'Sales Deal Desk',
      icon: HandshakeIcon,
      steps: [
        { label: 'Sales rep submits deal', duration: 800 },
        { label: 'Deal Validation Agent reviews terms', duration: 1500 },
        { label: 'Pricing compliance verified', duration: 1200 },
        { label: 'Approval workflow triggered', duration: 1000 },
        { label: 'Deal logged to UTG', duration: 800 },
      ],
    },
    'vendor-onboarding': {
      name: 'Vendor Onboarding',
      icon: Building2,
      steps: [
        { label: 'Vendor application received', duration: 800 },
        { label: 'Vendor Risk Agent assesses profile', duration: 1500 },
        { label: 'Compliance documents verified', duration: 1200 },
        { label: 'Contract Review Agent analyzes terms', duration: 1500 },
        { label: 'Vendor approved, logged to UTG', duration: 800 },
      ],
    },
  };

  const runSimulation = () => {
    if (isRunning) return;
    
    setIsRunning(true);
    setCurrentStep(-1);
    setCompleted(false);
    
    const workflow = workflows[selectedWorkflow];
    let totalDelay = 0;
    
    workflow.steps.forEach((step, index) => {
      setTimeout(() => {
        setCurrentStep(index);
      }, totalDelay);
      totalDelay += step.duration;
    });
    
    setTimeout(() => {
      setCompleted(true);
      setIsRunning(false);
    }, totalDelay + 500);
  };

  const workflow = workflows[selectedWorkflow];
  const WorkflowIcon = workflow.icon;

  return (
    <section id="workflow-simulator" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-[32px] font-bold text-[#0F172A] mb-4">
            Workflow Simulator
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">
            See how Liquicent orchestrates AI agents across enterprise workflows
          </p>
        </div>

        <Card 
          className="max-w-4xl mx-auto p-8 lg:p-10 bg-white border border-[#E5E7EB] rounded-xl"
          style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
        >
          {/* Controls */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
            <div className="flex items-center gap-4">
              <Select value={selectedWorkflow} onValueChange={setSelectedWorkflow} disabled={isRunning}>
                <SelectTrigger className="w-56 bg-white border-[#E5E7EB]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(workflows).map(([key, wf]) => (
                    <SelectItem key={key} value={key}>
                      {wf.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button 
              onClick={runSimulation}
              disabled={isRunning}
              className="bg-[#1A3A8F] hover:bg-[#142C6D] text-white"
            >
              {isRunning ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4 mr-2" />
                  Run Simulation
                </>
              )}
            </Button>
          </div>

          {/* Workflow Header */}
          <div className="flex items-center gap-3 mb-6 pb-6 border-b border-[#E5E7EB]">
            <div className="w-12 h-12 rounded-xl bg-[#1A3A8F]/10 flex items-center justify-center">
              <WorkflowIcon className="w-6 h-6 text-[#1A3A8F]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">{workflow.name}</h3>
              <p className="text-sm text-[#64748B]">{workflow.steps.length} steps</p>
            </div>
          </div>

          {/* Steps */}
          <div className="space-y-3">
            {workflow.steps.map((step, index) => (
              <div 
                key={index}
                className={`flex items-center gap-4 p-4 rounded-lg transition-all duration-500 ${
                  currentStep === index 
                    ? 'bg-[#2CB1A1]/10 border border-[#2CB1A1]/30' 
                    : currentStep > index
                      ? 'bg-[#1A3A8F]/5 border border-transparent'
                      : 'bg-[#F6F7FB] border border-transparent'
                }`}
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-500 ${
                  currentStep > index
                    ? 'bg-[#1A3A8F] text-white'
                    : currentStep === index
                      ? 'bg-[#2CB1A1] text-white'
                      : 'bg-[#E5E7EB] text-[#64748B]'
                }`}>
                  {currentStep > index ? (
                    <Check className="w-4 h-4" />
                  ) : currentStep === index ? (
                    <Zap className="w-4 h-4 animate-pulse" />
                  ) : (
                    <span className="text-sm font-medium">{index + 1}</span>
                  )}
                </div>
                
                <span className={`text-sm font-medium transition-colors duration-300 ${
                  currentStep >= index ? 'text-[#0F172A]' : 'text-[#64748B]'
                }`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* Completion Message */}
          {completed && (
            <div className="mt-6 p-6 bg-gradient-to-r from-[#1A3A8F]/5 to-[#2CB1A1]/5 rounded-xl border border-[#1A3A8F]/10">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#2CB1A1] flex items-center justify-center">
                  <Check className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">Workflow Complete</p>
                  <p className="text-sm text-[#475569]">Decision recorded in Unified Transaction Graph</p>
                </div>
              </div>
            </div>
          )}
        </Card>
      </div>
    </section>
  );
};

// Agent Registry Section - Updated with "Newton Engine" labeling
const AgentRegistrySection = () => {
  const nativeAgents = [
    { id: 'pr-verification-agent', name: 'PR Verification Agent', category: 'Governance', icon: GitBranch },
    { id: 'security-audit-agent', name: 'Security Audit Agent', category: 'Security', icon: ShieldCheck },
    { id: 'ticket-classification-agent', name: 'Ticket Classification Agent', category: 'Customer Ops', icon: MessageSquare },
    { id: 'deal-validation-agent', name: 'Deal Validation Agent', category: 'Sales Ops', icon: HandshakeIcon },
  ];

  const communityAgents = [
    { id: 'contract-review-agent', name: 'Contract Review Agent', category: 'Legal', icon: FileText, author: 'LegalTech Inc.' },
    { id: 'expense-validation-agent', name: 'Expense Validation Agent', category: 'Finance', icon: BarChart3, author: 'FinOps Labs' },
  ];

  return (
    <section className="py-24 bg-[#F6F7FB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between mb-12">
          <div>
            <h2 className="text-[32px] font-bold text-[#0F172A] mb-4">
              AI Agents for Enterprise Workflows
            </h2>
            <p className="text-lg text-[#475569] max-w-xl">
              Liquicent provides a registry of AI agents, including native Liquicent agents 
              and community agents built by third-party developers.
            </p>
          </div>
          <Link 
            to="/agents"
            className="mt-6 lg:mt-0 inline-flex items-center text-[#1A3A8F] font-medium hover:underline"
          >
            View all agents
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        {/* Native Agents - Powered by Newton Engine */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-[#1A3A8F] flex items-center justify-center">
              <Cpu className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">Liquicent Native Agents</h3>
              <p className="text-xs text-[#2CB1A1] font-medium">Powered by Newton Engine</p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {nativeAgents.map((agent) => {
              const AgentIcon = agent.icon;
              return (
                <Link key={agent.id} to={`/agents/${agent.id}`}>
                  <Card 
                    className="p-5 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-lg hover:border-[#1A3A8F]/30 transition-all h-full group"
                    style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <AgentIcon className="w-5 h-5 text-[#1A3A8F]" />
                      </div>
                      <span className="text-[10px] font-medium px-2 py-1 bg-[#1A3A8F]/10 text-[#1A3A8F] rounded-full">
                        Newton Engine
                      </span>
                    </div>
                    <h4 className="font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-[#1A3A8F] transition-colors">
                      {agent.name}
                    </h4>
                    <p className="text-xs text-[#64748B]">{agent.category}</p>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>

        {/* Community Agents */}
        <div>
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 rounded-lg bg-[#2CB1A1] flex items-center justify-center">
              <Users className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0F172A]">Community Agents</h3>
              <p className="text-xs text-[#64748B]">Built by third-party developers</p>
            </div>
          </div>
          
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {communityAgents.map((agent) => {
              const AgentIcon = agent.icon;
              return (
                <Link key={agent.id} to={`/agents/${agent.id}`}>
                  <Card 
                    className="p-5 bg-white border border-[#E5E7EB] rounded-xl hover:shadow-lg hover:border-[#2CB1A1]/30 transition-all h-full group"
                    style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="w-10 h-10 rounded-lg bg-[#2CB1A1]/10 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <AgentIcon className="w-5 h-5 text-[#2CB1A1]" />
                      </div>
                      <span className="text-[10px] font-medium px-2 py-1 bg-[#2CB1A1]/10 text-[#2CB1A1] rounded-full">
                        Community
                      </span>
                    </div>
                    <h4 className="font-semibold text-[#0F172A] text-sm mb-1 group-hover:text-[#2CB1A1] transition-colors">
                      {agent.name}
                    </h4>
                    <p className="text-xs text-[#64748B]">{agent.category} • by {agent.author}</p>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

// Platform Economics Section
const PlatformEconomicsSection = () => (
  <section className="py-24 bg-[#0F172A] text-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        <div>
          <h2 className="text-[32px] font-bold mb-6">
            Measure Coordination Cost Reduction
          </h2>
          <p className="text-lg text-[#94A3B8] mb-8 leading-relaxed">
            Traditional enterprises spend 40-60% of operational time on coordination overhead. 
            Liquicent's agent-driven workflows reduce this measurably over time.
          </p>
          
          <div className="space-y-4 mb-8">
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#2CB1A1]/20 flex items-center justify-center">
                <TrendingDown className="w-5 h-5 text-[#2CB1A1]" />
              </div>
              <p className="font-medium">60% reduction in approval cycle time</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#2CB1A1]/20 flex items-center justify-center">
                <BarChart3 className="w-5 h-5 text-[#2CB1A1]" />
              </div>
              <p className="font-medium">3x faster decision velocity</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="w-10 h-10 rounded-lg bg-[#2CB1A1]/20 flex items-center justify-center">
                <FileCheck className="w-5 h-5 text-[#2CB1A1]" />
              </div>
              <p className="font-medium">100% audit trail compliance</p>
            </div>
          </div>

          <Link 
            to="/platform-economics"
            className="inline-flex items-center text-[#2CB1A1] font-medium hover:underline"
          >
            Learn about Platform Economics
            <ChevronRight className="w-4 h-4 ml-1" />
          </Link>
        </div>

        {/* Convergence Graph */}
        <Card className="p-8 bg-white/5 border border-white/10 rounded-xl">
          <div className="flex items-center justify-between mb-6">
            <h4 className="text-sm font-medium text-[#94A3B8]">Coordination Cost Over Time</h4>
            <span className="text-xs text-[#2CB1A1] font-medium">Convergence Rate γ</span>
          </div>
          <div className="h-48 flex items-end justify-around gap-2">
            {[100, 82, 65, 50, 38, 28, 21, 16, 12, 10].map((height, i) => (
              <div 
                key={i}
                className="w-full bg-gradient-to-t from-[#1A3A8F] to-[#2CB1A1] rounded-t transition-all hover:opacity-80"
                style={{ height: `${height}%` }}
              />
            ))}
          </div>
          <div className="flex justify-between mt-4 text-xs text-[#64748B]">
            <span>Month 1</span>
            <span>Month 10</span>
          </div>
          <div className="mt-6 pt-4 border-t border-white/10 text-center">
            <p className="text-sm text-[#94A3B8]">Average <span className="text-[#2CB1A1] font-semibold">85% reduction</span> by Month 6</p>
          </div>
        </Card>
      </div>
    </div>
  </section>
);

// Developer Platform Section
const DeveloperPlatformSection = ({ navigate }) => (
  <section className="py-24 bg-white">
    <div className="max-w-7xl mx-auto px-6 lg:px-8">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="text-[32px] font-bold text-[#0F172A] mb-6">
            Build Agents on Liquicent
          </h2>
          <p className="text-lg text-[#475569] mb-8 leading-relaxed">
            Developers can create and publish AI agents that run on enterprise workflows. 
            Access APIs, use our Agent SDK, and reach enterprise customers.
          </p>
          
          <div className="grid grid-cols-2 gap-4 mb-8">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                <Network className="w-4 h-4 text-[#1A3A8F]" />
              </div>
              <span className="text-sm font-medium text-[#0F172A]">APIs</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                <Code2 className="w-4 h-4 text-[#1A3A8F]" />
              </div>
              <span className="text-sm font-medium text-[#0F172A]">Agent SDK</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                <Workflow className="w-4 h-4 text-[#1A3A8F]" />
              </div>
              <span className="text-sm font-medium text-[#0F172A]">Workflow Integration</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-[#1A3A8F]/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-[#1A3A8F]" />
              </div>
              <span className="text-sm font-medium text-[#0F172A]">Agent Publishing</span>
            </div>
          </div>

          <Button 
            onClick={() => navigate('/developers')}
            className="bg-[#1A3A8F] hover:bg-[#142C6D] text-white"
          >
            Start Building
            <ArrowRight className="ml-2 w-4 h-4" />
          </Button>
        </div>

        {/* Code Preview */}
        <Card className="p-6 bg-[#0F172A] rounded-xl overflow-hidden">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-3 h-3 rounded-full bg-[#EF4444]" />
            <div className="w-3 h-3 rounded-full bg-[#F59E0B]" />
            <div className="w-3 h-3 rounded-full bg-[#22C55E]" />
          </div>
          <pre className="text-sm text-[#E2E8F0] font-mono overflow-x-auto">
{`from liquicent import Agent, Workflow

class MyAgent(Agent):
    name = "my-custom-agent"
    
    async def execute(self, context):
        # Your agent logic here
        result = await self.process(context)
        
        # Record to UTG
        await self.record_decision(
            type="custom_decision",
            outcome=result
        )
        
        return result

# Deploy to Liquicent
agent = MyAgent()
await agent.deploy()`}
          </pre>
        </Card>
      </div>
    </div>
  </section>
);

// Research Section
const ResearchSection = () => {
  const topics = [
    { title: 'Unified Transaction Graph', path: '/research/unified-transaction-graph', description: 'The coordination graph that connects all decisions' },
    { title: 'DecisionTrace', path: '/research/decisiontrace', description: 'Explainable AI decisions for enterprise workflows' },
    { title: 'WorkRank', path: '/research/workrank', description: 'Prioritizing workflows based on organizational impact' },
    { title: 'SocioTrustRank', path: '/research/sociotrank', description: 'Trust scoring for AI agents in enterprise environments' },
  ];

  return (
    <section className="py-24 bg-[#F6F7FB]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-[32px] font-bold text-[#0F172A] mb-4">
            The Science Behind Liquicent
          </h2>
          <p className="text-lg text-[#475569] max-w-2xl mx-auto">
            Our platform is founded in research on organizational coordination, 
            transaction cost economics, and decision traceability.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {topics.map((topic) => (
            <Link 
              key={topic.path}
              to={topic.path}
              className="p-6 bg-white border border-[#E5E7EB] hover:border-[#1A3A8F]/30 rounded-xl transition-all group"
              style={{ boxShadow: '0px 6px 16px rgba(0,0,0,0.06)' }}
            >
              <h3 className="font-semibold text-[#0F172A] mb-2 group-hover:text-[#1A3A8F] transition-colors">
                {topic.title}
              </h3>
              <p className="text-sm text-[#64748B] mb-3">{topic.description}</p>
              <span className="text-sm text-[#1A3A8F] font-medium inline-flex items-center">
                Read paper
                <ChevronRight className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

// Final CTA
const FinalCTA = ({ navigate }) => (
  <section className="py-24 bg-gradient-to-br from-[#1A3A8F] to-[#142C6D] text-white">
    <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
      <h2 className="text-[32px] lg:text-[40px] font-bold mb-6">
        Deploy Liquicent In Your Organization
      </h2>
      <p className="text-lg text-white/80 mb-10 max-w-2xl mx-auto">
        Start with a pilot workflow and measure coordination improvements. 
        See real results in weeks, not months.
      </p>
      <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
        <Button 
          onClick={() => navigate('/request-demo')}
          className="bg-white text-[#1A3A8F] hover:bg-[#F6F7FB] px-8 py-6 text-base font-medium shadow-lg"
        >
          Request Demo
          <ArrowRight className="ml-2 w-5 h-5" />
        </Button>
        <Button 
          onClick={() => navigate('/request-demo')}
          variant="outline"
          className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-base font-medium"
        >
          Run Pilot
        </Button>
      </div>
    </div>
  </section>
);

export default HomeNew;
